// src/encounter/intelligence/engine-retry.ts
//
// One transient engine refusal never reaches the clinician as a failure line (Samer, 4 Nov, live on his visit: Next best
// action said "Scrips Intelligence is not available" and the next load answered 200). The card hooks (use-next-step,
// use-explain, use-brief) send each request through this: on 429, 502, 503 or 504, or a network failure, the request is sent
// again ONCE after 2 s, and a 429 once more after 5 s, while the card keeps its loading state. Anything else is final at once:
// a 200 (an answer, a withheld or a denied payload), 401, 403, 400, 404, and any other status. A cancelled request (A7) is
// never retried and stops waiting the moment it is cancelled. Pure apart from the timer; nothing logs.

/** The statuses a request is sent again for: rate limit, bad gateway, unavailable, gateway timeout. */
export const TRANSIENT_STATUS: ReadonlySet<number> = new Set([429, 502, 503, 504]);

/** Wait before each resend: the first resend after 2 s; a second, for 429 only, after 5 s more. */
export const RETRY_AFTER_MS = [2_000, 5_000] as const;

/** Resolves after `ms`, or at once when the request is cancelled. */
function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve();
    const t = setTimeout(done, ms);
    function done() {
      clearTimeout(t);
      signal.removeEventListener('abort', done);
      resolve();
    }
    signal.addEventListener('abort', done);
  });
}

/**
 * Runs `attempt`, and runs it again for a transient refusal (see the header). `statusOf` reads the HTTP status from what
 * `attempt` returns. A failure thrown by `attempt` (the network) counts as transient once; a cancelled request rethrows.
 */
export async function withTransientRetry<T>(attempt: () => Promise<T>, statusOf: (r: T) => number, signal: AbortSignal): Promise<T> {
  for (let resent = 0; ; resent++) {
    let result: T | undefined;
    let failure: unknown;
    let failed = false;
    try {
      result = await attempt();
    } catch (e) {
      if (signal.aborted) throw e;
      failure = e;
      failed = true;
    }
    const status = failed ? 0 : statusOf(result as T);
    const resends = status === 429 ? 2 : 1;
    if (!(failed || TRANSIENT_STATUS.has(status)) || resent >= resends) {
      if (failed) throw failure;
      return result as T;
    }
    await pause(RETRY_AFTER_MS[resent], signal);
    if (signal.aborted) throw new DOMException('The request was cancelled.', 'AbortError');
  }
}
